import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";

// Get URL from the environment variable or use fallback
const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL ||
  "https://mdrvhekhimhcwydrpueo.supabase.co";

/**
 * Helper function to call a Supabase Edge Function with proper authentication
 *
 * @param functionName The name of the edge function to call
 * @param payload The payload to send to the function
 * @returns The response from the edge function
 */
export const callEdgeFunction = async <T = any>(
  functionName: string,
  payload: any
): Promise<T> => {
  try {
    // Get the current user ID from the store
    const currentUserId = useProfileStore.getState().getUserId();

    // Ensure the user ID is always passed in the payload
    const payloadWithUser = {
      ...payload,
      createdBy: payload.createdBy || currentUserId,
    };

    // Verify we have a user ID before proceeding
    if (!payloadWithUser.createdBy) {
      throw new Error("User ID not available. Please log in again.");
    }

    // Get a fresh JWT token for authentication
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;

    if (!token) {
      throw new Error("No valid session. Please log in again.");
    }

    // Call the edge function with authentication
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/${functionName}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payloadWithUser),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        `Edge function error (${response.status}): ${
          errorData
            ? errorData.error || JSON.stringify(errorData)
            : response.statusText
        }`
      );
    }

    const data = await response.json();
    return data as T;
  } catch (error) {
    console.error(`Error calling edge function ${functionName}:`, error);
    throw error;
  }
};
